import { useCallback, useEffect, useRef, useState } from 'react'
import { api, ApiError } from '../lib/api.js'
import { podeNoPapel, dataHora } from '../lib/formato.js'
import { BannerLock } from '../componentes/BannerLock.jsx'
import { IndicadorSalvamento } from '../componentes/IndicadorSalvamento.jsx'
import { AvisoPlaceholders } from '../componentes/AvisoPlaceholders.jsx'

const INTERVALO_AUTOSAVE_MS = 10_000

export function Editor({ agente, papel, usuario }) {
  const [carga, setCarga] = useState(null)
  const [vigente, setVigente] = useState(null)
  const [conteudo, setConteudo] = useState('')
  const [atualizadoEm, setAtualizadoEm] = useState(null)
  const [lock, setLock] = useState(null)
  const [sujo, setSujo] = useState(false)
  const [estado, setEstado] = useState('salvo')
  const [conflito, setConflito] = useState(null)
  const [erro, setErro] = useState(null)
  const [ocupado, setOcupado] = useState(false)
  const [publicacao, setPublicacao] = useState(null)
  const [publicado, setPublicado] = useState(null)

  // Relogio unico da tela: decide se o lock expirou, e disso dependem tanto o
  // banner quanto o bloqueio do botao Publicar.
  const [agora, setAgora] = useState(() => Date.now())
  useEffect(() => {
    const tique = setInterval(() => setAgora(Date.now()), 1000)
    return () => clearInterval(tique)
  }, [])

  const podeEditar = podeNoPapel(papel, 'editor')
  const podePublicar = podeNoPapel(papel, 'publicador')
  const tenhoLock = lock?.detentor === usuario.email
  const lockExpirado = lock ? agora >= Date.parse(lock.expiraEm) : true
  const editavel = podeEditar && tenhoLock && estado !== 'conflito'

  // Alguem esta editando AGORA: o texto ainda pode mudar, entao publicar seria
  // aprovar um alvo movel. Lock expirado ou livre nao bloqueia — o publicador
  // continua podendo publicar o rascunho deixado pelo editor.
  const lockAtivoDeOutro = Boolean(lock) && !tenhoLock && !lockExpirado
  const identicoAoVigente = conteudo === (vigente?.conteudo ?? null)
  const publicarBloqueadoPor = lockAtivoDeOutro
    ? `Em edição por ${lock.detentor} — aguarde a conclusão`
    : identicoAoVigente
      ? 'O rascunho é idêntico à versão vigente — não há o que publicar'
      : estado === 'conflito'
        ? 'Resolva o conflito antes de publicar'
        : null

  const carregar = useCallback(async () => {
    setErro(null)
    try {
      const [rascunho, publicada] = await Promise.all([
        api.get(`/agentes/${agente}/rascunho`),
        api.get(`/agentes/${agente}/vigente`).catch((falha) => {
          if (falha instanceof ApiError && falha.status === 404) return null // agente sem versao publicada
          throw falha
        }),
      ])

      setCarga(rascunho)
      setVigente(publicada)
      setConteudo(rascunho.conteudo)
      setAtualizadoEm(rascunho.atualizadoEm)
      setLock(rascunho.lock)
      setSujo(false)
      setConflito(null)
      setEstado('salvo')
    } catch (falha) {
      setErro(falha.message)
    }
  }, [agente])

  useEffect(() => {
    carregar()
  }, [carregar])

  // Ref para o autosave enxergar o estado atual sem se reinscrever a cada tecla.
  const atual = useRef({})
  atual.current = { conteudo, atualizadoEm, sujo, tenhoLock, estado }

  /** Devolve o novo `atualizadoEm` em caso de sucesso, ou null se falhou. */
  const salvar = useCallback(async () => {
    const { conteudo: texto, atualizadoEm: token } = atual.current
    setEstado('salvando')
    try {
      const resposta = await api.put(`/agentes/${agente}/rascunho`, { conteudo: texto, atualizadoEm: token })
      setAtualizadoEm(resposta.atualizadoEm)
      setLock(resposta.lock)
      // So limpa o "sujo" se ninguem digitou durante o request.
      const mudouNoMeio = atual.current.conteudo !== texto
      setSujo(mudouNoMeio)
      setEstado(mudouNoMeio ? 'sujo' : 'salvo')
      return resposta.atualizadoEm
    } catch (falha) {
      if (falha instanceof ApiError && falha.codigo === 'conflito_rascunho') {
        setConflito(falha.detalhes ?? {})
        setEstado('conflito')
        return null
      }
      if (falha instanceof ApiError && (falha.codigo === 'lock_de_outro' || falha.status === 403)) {
        // Lock expirou e outro assumiu: volta para leitura mostrando quem esta la.
        setLock(falha.detalhes?.detentor ? falha.detalhes : null)
        setEstado('erro')
        setErro(falha.message)
        return null
      }
      setEstado('erro')
      return null
    }
  }, [agente])

  useEffect(() => {
    const tique = setInterval(() => {
      const { sujo: temMudanca, tenhoLock: comLock, estado: situacao } = atual.current
      if (temMudanca && comLock && situacao !== 'conflito' && situacao !== 'salvando') salvar()
    }, INTERVALO_AUTOSAVE_MS)
    return () => clearInterval(tique)
  }, [salvar])

  async function assumirEdicao() {
    setOcupado(true)
    setErro(null)
    try {
      const resposta = await api.post(`/agentes/${agente}/rascunho/checkout`)
      setLock({ detentor: resposta.detentor, expiraEm: resposta.expiraEm })
    } catch (falha) {
      if (falha instanceof ApiError && falha.codigo === 'lock_ocupado') setLock(falha.detalhes)
      setErro(falha.message)
    } finally {
      setOcupado(false)
    }
  }

  /** Check-in: devolve o lock na hora, sem fazer os outros esperarem o TTL. */
  async function concluirEdicao() {
    setOcupado(true)
    setErro(null)
    try {
      if (atual.current.sujo) {
        // Concluir com texto pendente perderia a digitacao: salva antes.
        if ((await salvar()) === null) return
      }
      await api.post(`/agentes/${agente}/rascunho/checkin`)
      await carregar()
    } catch (falha) {
      setErro(falha.message)
    } finally {
      setOcupado(false)
    }
  }

  async function publicar(confirmaAvisos) {
    setOcupado(true)
    try {
      // Publicar envia o RASCUNHO DO BANCO. Se ha texto nao salvo, salva antes
      // — senao o usuario publicaria uma versao anterior a que esta vendo.
      // O token vem do proprio save: `atualizadoEm` do estado ainda nao
      // reagiu ao setState desta mesma volta.
      let token = atual.current.atualizadoEm
      if (atual.current.sujo) {
        token = await salvar()
        if (token === null) return
      }

      const resposta = await api.post(`/agentes/${agente}/publicar`, {
        comentario: publicacao.comentario.trim() || null,
        atualizadoEm: token,
        ...(confirmaAvisos ? { confirmaAvisos: true } : {}),
      })
      setPublicacao(null)
      await carregar()
      setErro(null)
      setPublicado(`Versão ${resposta.versao} publicada.`)
    } catch (falha) {
      if (falha instanceof ApiError && falha.codigo === 'avisos_pendentes') {
        setPublicacao((anterior) => ({
          ...anterior,
          avisos: falha.detalhes?.placeholdersAusentes ?? [],
          mensagem: falha.message,
        }))
        return
      }
      if (falha instanceof ApiError && falha.codigo === 'rascunho_alterado') {
        setPublicacao((anterior) => ({ ...anterior, desatualizado: falha.detalhes ?? {}, erro: falha.message }))
        return
      }
      setPublicacao((anterior) => ({ ...anterior, erro: falha.message }))
    } finally {
      setOcupado(false)
    }
  }

  if (erro && !carga) return <p className="alerta alerta--falha">{erro}</p>
  if (!carga) return <p className="ajuda">Carregando…</p>

  return (
    <section className="editor">
      <div className="editor__barra">
        <div className="editor__meta">
          <strong>{agente}</strong>
          <span className="etiqueta">{papel}</span>
          {vigente ? (
            <span className="ajuda">
              vigente: v{vigente.versao} · {dataHora(vigente.criado_em)}
            </span>
          ) : (
            <span className="ajuda">sem versão publicada</span>
          )}
        </div>

        <div className="editor__acoes">
          <IndicadorSalvamento
            estado={sujo && estado === 'salvo' ? 'sujo' : estado}
            atualizadoEm={atualizadoEm}
            conflito={conflito}
            aoRecarregar={carregar}
          />
          {podeEditar && !tenhoLock && !lock && (
            <button className="botao botao--primario" onClick={assumirEdicao} disabled={ocupado}>
              Editar
            </button>
          )}
          {editavel && (
            <button className="botao" onClick={salvar} disabled={!sujo || estado === 'salvando'}>
              Salvar agora
            </button>
          )}
          {tenhoLock && podeEditar && (
            <button
              className="botao"
              onClick={concluirEdicao}
              disabled={ocupado || estado === 'salvando'}
              title="Libera o bloqueio agora, sem esperar os 15 minutos"
            >
              {ocupado ? 'Concluindo…' : 'Concluir edição'}
            </button>
          )}
          {podePublicar && (
            <button
              className="botao botao--primario"
              onClick={() => setPublicacao({ comentario: '' })}
              disabled={ocupado || publicarBloqueadoPor !== null}
              title={publicarBloqueadoPor ?? 'Publicar o rascunho como nova versão'}
            >
              Publicar
            </button>
          )}
        </div>
      </div>

      {!tenhoLock && (
        <BannerLock
          lock={lock}
          expirado={lockExpirado}
          podeEditar={podeEditar}
          podePublicar={podePublicar}
          aoAssumir={assumirEdicao}
          ocupado={ocupado}
        />
      )}

      {publicado && (
        <div className="alerta alerta--ok">
          {publicado}{' '}
          <button className="botao botao--pequeno" onClick={() => setPublicado(null)}>
            ok
          </button>
        </div>
      )}
      {erro && carga && <p className="alerta alerta--falha">{erro}</p>}

      <AvisoPlaceholders conteudo={conteudo} />

      <textarea
        className="editor__texto"
        value={conteudo}
        readOnly={!editavel}
        spellCheck={false}
        onChange={(evento) => {
          setConteudo(evento.target.value)
          setSujo(true)
          if (estado === 'erro') setEstado('salvo')
        }}
        placeholder="Conteúdo do prompt…"
      />

      <div className="editor__rodape">
        <span className="ajuda">
          {conteudo.length.toLocaleString('pt-BR')} caracteres
          {carga.existe
            ? ` · rascunho de ${carga.atualizadoPor}`
            : ` · novo rascunho${carga.baseadoNaVersao ? ` (base: v${carga.baseadoNaVersao})` : ''}`}
        </span>
        {editavel && <span className="ajuda">Autosave a cada 10s quando há alterações</span>}
      </div>

      {publicacao && (
        <PainelPublicar
          publicacao={publicacao}
          ocupado={ocupado}
          aoMudarComentario={(comentario) => setPublicacao((anterior) => ({ ...anterior, comentario }))}
          aoCancelar={() => setPublicacao(null)}
          aoPublicar={publicar}
          aoRecarregar={async () => {
            setPublicacao(null)
            await carregar()
          }}
        />
      )}
    </section>
  )
}

function PainelPublicar({ publicacao, ocupado, aoMudarComentario, aoCancelar, aoPublicar, aoRecarregar }) {
  const temAvisos = Array.isArray(publicacao.avisos) && publicacao.avisos.length > 0
  const desatualizado = publicacao.desatualizado

  return (
    <div className="painel">
      <h3>Publicar nova versão</h3>

      {desatualizado ? (
        // Nao adianta oferecer "publicar mesmo assim": o texto revisado nao e
        // mais o que esta no banco. O caminho e recarregar e reler o diff.
        <>
          <div className="alerta alerta--falha">
            <strong>Rascunho alterado.</strong> {publicacao.erro}
            {desatualizado.atualizadoPor && (
              <div className="banner__detalhe">Último salvamento: {desatualizado.atualizadoPor}</div>
            )}
          </div>
          <div className="linha-acoes">
            <button className="botao botao--primario" onClick={aoRecarregar} disabled={ocupado}>
              Recarregar e revisar o diff
            </button>
            <button className="botao botao--discreto" onClick={aoCancelar} disabled={ocupado}>
              Cancelar
            </button>
          </div>
        </>
      ) : (
        <>
          <label className="campo">
            <span>Comentário (opcional)</span>
            <input
              value={publicacao.comentario}
              onChange={(evento) => aoMudarComentario(evento.target.value)}
              placeholder="ex.: ajuste de tom no encerramento"
              autoFocus
            />
          </label>

          {temAvisos && (
            <div className="alerta alerta--aviso">
              <strong>Atenção:</strong> o texto não contém{' '}
              {publicacao.avisos.map((p) => (
                <code key={p}>{p}</code>
              ))}
              . O agente vai enviar esse trecho literalmente. Publicar mesmo assim?
            </div>
          )}
          {publicacao.erro && <div className="alerta alerta--falha">{publicacao.erro}</div>}

          <div className="linha-acoes">
            <button className="botao botao--primario" onClick={() => aoPublicar(temAvisos)} disabled={ocupado}>
              {ocupado ? 'Publicando…' : temAvisos ? 'Publicar mesmo assim' : 'Publicar'}
            </button>
            <button className="botao botao--discreto" onClick={aoCancelar} disabled={ocupado}>
              Cancelar
            </button>
          </div>
        </>
      )}
    </div>
  )
}
